// Delivery Performance Dashboard — KPIs, driver rankings, zone stats, trend chart
import { useCallback } from "react";
import {
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  AlertCircle,
  Download,
} from "lucide-react";
import { Button, Skeleton } from "@cieda/djayma-ui";
import { format } from "date-fns";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard } from "@/components/admin/shared/KPICard";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { DateRangePicker } from "@/components/admin/shared/DateRangePicker";
import type { DateRange } from "@/components/admin/shared/DateRangePicker";
import { ExportButton } from "@/components/admin/shared/ExportButton";
import { useDeliveryPerformance } from "@/hooks/admin/useDeliveryPerformance";
import type {
  DeliveryDriverStat,
  DeliveryZoneStat,
  DeliveryTrendPoint,
} from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMinutes(minutes: number | null): string {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? `${m}min` : ""}` : `${m}min`;
}

function formatPercent(rate: number | null): string {
  if (rate == null) return "—";
  return `${Math.round(rate * 100)}%`;
}

function formatFcfa(amount: number): string {
  return new Intl.NumberFormat("fr-SN").format(Math.round(amount));
}

// ---------------------------------------------------------------------------
// Trend chart — CSS bar chart, no external library
// ---------------------------------------------------------------------------

interface TrendChartProps {
  readonly data: readonly DeliveryTrendPoint[];
}

function TrendChart({ data }: TrendChartProps) {
  if (data.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-stone-400">
        Pas de données
      </div>
    );
  }

  const maxTotal = Math.max(...data.map((d) => d.total), 1);

  return (
    <div className="overflow-x-auto">
      <div
        className="flex min-w-max items-end gap-1 px-1"
        style={{ height: "120px" }}
      >
        {data.map((point) => {
          const barHeight = Math.max((point.total / maxTotal) * 100, 2);
          const deliveredPct = point.total > 0 ? point.delivered / point.total : 0;

          return (
            <div
              key={point.date}
              className="group relative flex flex-col items-center gap-1"
              style={{ minWidth: "28px" }}
            >
              {/* Bar */}
              <div
                className="w-full rounded-t-sm bg-stone-200 transition-all"
                style={{ height: `${barHeight}%` }}
                title={`${point.date} — ${point.total} livraisons (${formatPercent(deliveredPct)} livrées)`}
              >
                <div
                  className="w-full rounded-t-sm bg-amber-400 transition-all"
                  style={{ height: `${deliveredPct * 100}%` }}
                />
              </div>
              {/* Date label */}
              <span className="text-[9px] text-stone-400">
                {point.date.slice(5)} {/* MM-DD */}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-3 px-1">
        <span className="flex items-center gap-1 text-xs text-stone-500">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" />
          Livrées
        </span>
        <span className="flex items-center gap-1 text-xs text-stone-500">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-stone-200" />
          Échouées / En cours
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Driver stats table columns
// ---------------------------------------------------------------------------

const DRIVER_COLUMNS: readonly ColumnDef<DeliveryDriverStat & { rank: number }>[] = [
  {
    id: "rank",
    header: "#",
    cell: (d) => (
      <span className="font-medium text-stone-500">{d.rank}</span>
    ),
  },
  {
    id: "name",
    header: "Chauffeur",
    cell: (d) => (
      <span className="font-medium text-stone-900">{d.name}</span>
    ),
  },
  {
    id: "delivered",
    header: "Livrées",
    cell: (d) => (
      <span className="text-emerald-700">{d.delivered}</span>
    ),
  },
  {
    id: "failed",
    header: "Échouées",
    cell: (d) => (
      <span className="text-red-600">{d.failed}</span>
    ),
  },
  {
    id: "total",
    header: "Total",
    cell: (d) => <span>{d.total}</span>,
  },
  {
    id: "success_rate",
    header: "Taux de succès",
    cell: (d) => (
      <div className="flex items-center gap-2">
        <div className="h-1.5 w-20 rounded-full bg-stone-100">
          <div
            className="h-1.5 rounded-full bg-amber-400"
            style={{ width: `${Math.round(d.success_rate * 100)}%` }}
          />
        </div>
        <span className="text-sm font-medium text-stone-700">
          {formatPercent(d.success_rate)}
        </span>
      </div>
    ),
  },
  {
    id: "avg_delay",
    header: "Délai moyen",
    cell: (d) => (
      <span className="text-stone-600">
        {formatMinutes(d.avg_delay_minutes)}
      </span>
    ),
  },
];

// ---------------------------------------------------------------------------
// Zone stats table columns
// ---------------------------------------------------------------------------

const ZONE_COLUMNS: readonly ColumnDef<DeliveryZoneStat>[] = [
  {
    id: "zone",
    header: "Zone",
    cell: (z) => (
      <span className="font-medium text-stone-900">{z.zone}</span>
    ),
  },
  {
    id: "total",
    header: "Total",
    cell: (z) => <span>{z.total}</span>,
  },
  {
    id: "delivered",
    header: "Livrées",
    cell: (z) => <span className="text-emerald-700">{z.delivered}</span>,
  },
  {
    id: "on_time_rate",
    header: "À l'heure",
    cell: (z) => (
      <span className="font-medium text-stone-700">
        {formatPercent(z.on_time_rate)}
      </span>
    ),
  },
  {
    id: "avg_minutes",
    header: "Délai moyen",
    cell: (z) => (
      <span className="text-stone-600">
        {formatMinutes(z.avg_delivery_minutes)}
      </span>
    ),
  },
  {
    id: "cost_total",
    header: "Coût total",
    cell: (z) => (
      <span className="text-stone-700">{formatFcfa(z.cost_total)} FCFA</span>
    ),
  },
];

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function DeliveryPerformance() {
  const {
    data,
    isLoading,
    isError,
    refetch,
    dateRange,
    setDateRange,
    params,
  } = useDeliveryPerformance();

  const handleDateChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
    },
    [setDateRange],
  );

  const summary = data?.summary;
  const driverStats = (data?.driver_performance ?? []).map((d, i) => ({
    ...d,
    rank: i + 1,
  }));
  const zoneStats = data?.zone_performance ?? [];
  const trend = data?.trend ?? [];

  // Export data
  const exportDriverData = driverStats.map((d) => ({
    rang: d.rank,
    chauffeur: d.name,
    livrées: d.delivered,
    échouées: d.failed,
    total: d.total,
    taux_succès: `${Math.round(d.success_rate * 100)}%`,
    délai_moyen: formatMinutes(d.avg_delay_minutes),
  }));

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Performance livraison
              </h1>
              <p className="text-sm text-stone-500">
                {params.from} – {params.to}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <DateRangePicker value={dateRange} onChange={handleDateChange} />
            {data && (
              <ExportButton
                data={exportDriverData}
                columns={[
                  { key: "rang", label: "#" },
                  { key: "chauffeur", label: "Chauffeur" },
                  { key: "livrées", label: "Livrées" },
                  { key: "échouées", label: "Échouées" },
                  { key: "total", label: "Total" },
                  { key: "taux_succès", label: "Taux succès" },
                  { key: "délai_moyen", label: "Délai moyen" },
                ]}
                filename={`performance-livraison-${format(dateRange.from, "yyyy-MM-dd")}`}
              />
            )}
          </div>
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les données de performance.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {isLoading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="rounded-lg border border-stone-200 bg-white p-5"
                >
                  <Skeleton className="mb-2 h-4 w-28" />
                  <Skeleton className="h-7 w-20" />
                </div>
              ))}
            </>
          ) : isError ? (
            // Error state — show placeholder cards so skeletons don't linger
            <>
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="rounded-lg border border-red-100 bg-red-50 p-5"
                >
                  <p className="text-xs text-red-400">—</p>
                </div>
              ))}
            </>
          ) : summary ? (
            <>
              <KPICard
                label="Taux de livraison à temps"
                value={
                  summary.on_time_rate != null
                    ? Math.round(summary.on_time_rate * 100)
                    : 0
                }
                format="percent"
                icon={<CheckCircle className="h-5 w-5" />}
              />
              <KPICard
                label="Délai moyen"
                value={formatMinutes(summary.avg_delivery_minutes)}
                icon={<Clock className="h-5 w-5" />}
              />
              <KPICard
                label="Taux d'échec"
                value={
                  summary.total_deliveries > 0
                    ? Math.round(
                        (summary.failed / summary.total_deliveries) * 100,
                      )
                    : 0
                }
                format="percent"
                icon={<XCircle className="h-5 w-5" />}
              />
              <KPICard
                label="Coût moyen / livraison"
                value={
                  summary.cost_per_delivery != null
                    ? Math.round(summary.cost_per_delivery)
                    : 0
                }
                format="currency"
                icon={<DollarSign className="h-5 w-5" />}
              />
            </>
          ) : null}
        </div>

        {/* Secondary KPIs */}
        {!isLoading && summary && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-stone-200 bg-white px-4 py-3">
              <p className="text-xs text-stone-500">Total livraisons</p>
              <p className="mt-1 text-xl font-semibold text-stone-900">
                {summary.total_deliveries}
              </p>
            </div>
            <div className="rounded-lg border border-stone-200 bg-white px-4 py-3">
              <p className="text-xs text-stone-500">Livrées</p>
              <p className="mt-1 text-xl font-semibold text-emerald-700">
                {summary.delivered}
              </p>
            </div>
            <div className="rounded-lg border border-stone-200 bg-white px-4 py-3">
              <p className="text-xs text-stone-500">En cours</p>
              <p className="mt-1 text-xl font-semibold text-amber-700">
                {summary.in_progress}
              </p>
            </div>
            <div className="rounded-lg border border-stone-200 bg-white px-4 py-3">
              <p className="text-xs text-stone-500">Coût total</p>
              <p className="mt-1 text-xl font-semibold text-stone-900">
                {formatFcfa(summary.total_cost)} FCFA
              </p>
            </div>
          </div>
        )}

        {/* Trend chart */}
        {!isLoading && trend.length > 0 && (
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-semibold text-stone-700">
              Évolution des livraisons
            </h2>
            <TrendChart data={trend} />
          </div>
        )}

        {/* Driver performance table */}
        <div className="rounded-lg border border-stone-200 bg-white">
          <div className="border-b border-stone-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-stone-700">
              Performance par chauffeur
            </h2>
          </div>
          <div className="p-4">
            <AdminDataTable
              data={driverStats}
              columns={DRIVER_COLUMNS}
              isLoading={isLoading}
              emptyMessage="Aucune donnée chauffeur pour cette période."
              getRowId={(d) => d.driver_id}
            />
          </div>
        </div>

        {/* Zone performance table */}
        <div className="rounded-lg border border-stone-200 bg-white">
          <div className="border-b border-stone-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-stone-700">
              Performance par zone
            </h2>
          </div>
          <div className="p-4">
            <AdminDataTable
              data={zoneStats}
              columns={ZONE_COLUMNS}
              isLoading={isLoading}
              emptyMessage="Aucune donnée par zone pour cette période."
              getRowId={(z) => z.zone}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
